import React, { useState, useCallback, useEffect, useRef } from 'react';
import BarLayout from './components/BarLayout';
import ProductPicker from './components/ProductPicker';
import barConfig from './data/barConfig';
import products from './data/products.json';

const API = process.env.REACT_APP_API_URL || '/api/bar3d';

// Palette de blocs (panneau lateral)
function BlockPalette({ blockTypes, onAddBlock }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '10px',
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      minWidth: '160px',
    }}>
      <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>
        Ajouter un bloc
      </div>
      {blockTypes.map(bt => (
        <button
          key={bt.type}
          onClick={() => onAddBlock(bt)}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 12px',
            background: bt.color,
            border: `2px solid ${bt.borderColor}`,
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 'bold',
            color: bt.borderColor,
            transition: 'transform 0.1s',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <span style={{ fontSize: '16px' }}>{bt.icon}</span>
          <span>{bt.label}</span>
          {bt.levels > 0 && (
            <span style={{ fontSize: '10px', opacity: 0.7 }}>
              {bt.levels}x{bt.slotsPerLevel}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// Selecteur de layout sauvegarde
function LayoutSelector({ layouts, currentId, onSelect, onNew, onDelete }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <select
        value={currentId || ''}
        onChange={(e) => onSelect(e.target.value ? parseInt(e.target.value) : null)}
        style={{
          padding: '6px 10px', border: '1px solid #ccc', borderRadius: '6px',
          fontSize: '13px', background: '#fff', cursor: 'pointer',
        }}
      >
        <option value="">-- Choisir un bar --</option>
        {layouts.map(l => (
          <option key={l.id} value={l.id}>{l.nom}</option>
        ))}
      </select>
      <button onClick={onNew} style={{
        padding: '6px 12px', background: '#1565c0', color: '#fff',
        border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold',
      }}>
        + Nouveau
      </button>
      {currentId && (
        <button onClick={() => onDelete(currentId)} style={{
          padding: '6px 10px', background: '#fff', color: '#c62828',
          border: '1px solid #c62828', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
        }}>
          Supprimer
        </button>
      )}
    </div>
  );
}

function App() {
  // Blocs places sur la grille
  const [blocks, setBlocks] = useState(barConfig.blocks);
  // Emplacements remplis
  const [slots, setSlots] = useState({});
  // Popup produit
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  // Mode edition
  const [editMode, setEditMode] = useState(true);
  // Compteur ID blocs
  const [blockIdCounter, setBlockIdCounter] = useState(10);
  // Layouts MySQL
  const [layouts, setLayouts] = useState([]);
  const [currentLayoutId, setCurrentLayoutId] = useState(null);
  const [layoutName, setLayoutName] = useState('Mon Bar');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [dbConnected, setDbConnected] = useState(true);

  // Auto-save timer
  const saveTimer = useRef(null);

  // Charger la liste des layouts au demarrage
  useEffect(() => {
    fetchLayouts();
  }, []);

  const fetchLayouts = async () => {
    try {
      const res = await fetch(`${API}/layouts`);
      if (!res.ok) throw new Error('Serveur non disponible');
      const data = await res.json();
      setLayouts(data);
      setDbConnected(true);
      // Charger le dernier layout automatiquement
      if (data.length > 0 && !currentLayoutId) {
        loadLayout(data[0].id);
      }
    } catch (err) {
      console.error('API non disponible:', err);
      setDbConnected(false);
    }
  };

  // Charger un layout
  const loadLayout = async (id) => {
    try {
      const res = await fetch(`${API}/layouts/${id}`);
      const data = await res.json();
      setBlocks(data.blocks || []);
      setSlots(data.slots || {});
      setLayoutName(data.nom);
      setCurrentLayoutId(data.id);
      // Calculer le prochain ID de bloc
      let maxId = 10;
      (data.blocks || []).forEach(b => {
        const parts = b.id.split('-');
        const num = parseInt(parts[parts.length - 1]);
        if (!isNaN(num) && num >= maxId) maxId = num + 1;
      });
      setBlockIdCounter(maxId);
      setSaveStatus('Charge');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (err) {
      console.error('Erreur chargement:', err);
    }
  };

  // Sauvegarder
  const saveLayout = useCallback(async (blocksToSave, slotsToSave, name) => {
    if (!dbConnected) return;
    setSaving(true);
    try {
      const body = {
        nom: name || layoutName,
        blocks: blocksToSave || blocks,
        slots: slotsToSave || slots,
        grid_cols: barConfig.gridCols,
        grid_rows: barConfig.gridRows,
      };

      if (currentLayoutId) {
        await fetch(`${API}/layouts/${currentLayoutId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        const res = await fetch(`${API}/layouts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        setCurrentLayoutId(data.id);
        fetchLayouts();
      }
      setSaveStatus('Sauvegarde !');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setSaveStatus('Erreur !');
      setTimeout(() => setSaveStatus(''), 3000);
    }
    setSaving(false);
  }, [currentLayoutId, blocks, slots, layoutName, dbConnected]);

  // Auto-save quand blocks ou slots changent
  useEffect(() => {
    if (!currentLayoutId || !dbConnected) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveLayout(blocks, slots, layoutName);
    }, 2000); // sauvegarde 2s apres le dernier changement
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [blocks, slots, currentLayoutId, dbConnected, saveLayout, layoutName]);

  // Nouveau layout
  const handleNewLayout = async () => {
    const nom = prompt('Nom du bar :', 'Mon Bar');
    if (!nom) return;
    setBlocks(barConfig.blocks);
    setSlots({});
    setLayoutName(nom);
    setCurrentLayoutId(null);
    setBlockIdCounter(10);
    // Sauvegarder immediatement
    try {
      const res = await fetch(`${API}/layouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom,
          blocks: barConfig.blocks,
          slots: {},
          grid_cols: barConfig.gridCols,
          grid_rows: barConfig.gridRows,
        }),
      });
      const data = await res.json();
      setCurrentLayoutId(data.id);
      fetchLayouts();
    } catch (err) {
      console.error('Erreur creation:', err);
    }
  };

  // Supprimer un layout
  const handleDeleteLayout = async (id) => {
    if (!window.confirm('Supprimer ce layout ?')) return;
    try {
      await fetch(`${API}/layouts/${id}`, { method: 'DELETE' });
      if (currentLayoutId === id) {
        setCurrentLayoutId(null);
        setBlocks(barConfig.blocks);
        setSlots({});
        setLayoutName('Mon Bar');
      }
      fetchLayouts();
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
  };

  // Selectionner un layout
  const handleSelectLayout = (id) => {
    if (id) loadLayout(id);
  };

  // Clic sur un emplacement
  const handleSlotClick = useCallback((slotId) => {
    setSelectedSlotId(slotId);
    setPickerOpen(true);
  }, []);

  // Affecter un produit
  const handleAssignProduct = useCallback((product, quantite) => {
    if (selectedSlotId) {
      setSlots(prev => ({
        ...prev,
        [selectedSlotId]: { product, quantite },
      }));
    }
    setPickerOpen(false);
    setSelectedSlotId(null);
  }, [selectedSlotId]);

  // Vider un emplacement
  const handleClearSlot = useCallback(() => {
    if (selectedSlotId) {
      setSlots(prev => {
        const next = { ...prev };
        delete next[selectedSlotId];
        return next;
      });
    }
    setPickerOpen(false);
    setSelectedSlotId(null);
  }, [selectedSlotId]);

  // Ajouter un bloc
  const handleAddBlock = useCallback((blockType) => {
    const newId = `${blockType.type}-${blockIdCounter}`;
    setBlockIdCounter(prev => prev + 1);
    setBlocks(prev => [
      ...prev,
      {
        id: newId,
        type: blockType.type,
        label: blockType.label,
        x: 1,
        y: 1,
        width: blockType.width,
        height: blockType.height,
        levels: blockType.levels || 0,
        slotsPerLevel: blockType.slotsPerLevel || 0,
      },
    ]);
  }, [blockIdCounter]);

  // Supprimer un bloc
  const handleDeleteBlock = useCallback((blockId) => {
    setBlocks(prev => prev.filter(b => b.id !== blockId));
    setSlots(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        if (key.startsWith(blockId + '-')) delete next[key];
      });
      return next;
    });
  }, []);

  // Stats
  const totalSlots = blocks.reduce((s, b) => s + (b.levels || 0) * (b.slotsPerLevel || 0), 0);
  const filledSlots = Object.keys(slots).length;
  const totalQuantite = Object.values(slots).reduce((s, v) => s + v.quantite, 0);

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#f5f0eb', fontFamily: 'Arial, sans-serif', overflow: 'auto' }}>

      {/* Header */}
      <div style={{
        background: '#fff', padding: '10px 24px', borderBottom: '1px solid #e0e0e0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: '10px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1 style={{ margin: 0, fontSize: '18px', color: '#333' }}>
            Bar 3D
          </h1>
          {/* Nom editable */}
          {currentLayoutId && (
            <input
              value={layoutName}
              onChange={(e) => setLayoutName(e.target.value)}
              style={{
                padding: '4px 10px', border: '1px solid #ddd', borderRadius: '6px',
                fontSize: '14px', fontWeight: 'bold', color: '#1565c0', width: '180px',
              }}
            />
          )}
          {/* Status sauvegarde */}
          <span style={{
            fontSize: '11px',
            color: saving ? '#ff9800' : saveStatus === 'Erreur !' ? '#c62828' : '#4caf50',
            fontWeight: 'bold',
          }}>
            {saving ? 'Sauvegarde...' : saveStatus}
          </span>
          {!dbConnected && (
            <span style={{
              fontSize: '11px', color: '#c62828', background: '#ffebee',
              padding: '2px 8px', borderRadius: '4px',
            }}>
              DB hors ligne
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '13px', flexWrap: 'wrap' }}>
          {/* Selecteur layout */}
          <LayoutSelector
            layouts={layouts}
            currentId={currentLayoutId}
            onSelect={handleSelectLayout}
            onNew={handleNewLayout}
            onDelete={handleDeleteLayout}
          />

          <span>Emplacements: <b style={{ color: '#1565c0' }}>{filledSlots}/{totalSlots}</b></span>
          <span>Produits: <b style={{ color: '#2e7d32' }}>{totalQuantite}</b></span>

          {/* Sauvegarde manuelle */}
          <button
            onClick={() => saveLayout()}
            disabled={saving || !dbConnected}
            style={{
              padding: '6px 14px', background: '#4caf50', color: '#fff',
              border: 'none', borderRadius: '6px', fontSize: '12px',
              cursor: saving ? 'default' : 'pointer', fontWeight: 'bold',
              opacity: saving ? 0.6 : 1,
            }}
          >
            Sauvegarder
          </button>

          {/* Toggle mode */}
          <button
            onClick={() => setEditMode(!editMode)}
            style={{
              padding: '6px 16px',
              background: editMode ? '#ff9800' : '#4caf50',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            {editMode ? 'Mode Edition' : 'Mode Normal'}
          </button>
        </div>
      </div>

      {/* Contenu principal */}
      <div style={{ display: 'flex', gap: '12px', padding: '12px', alignItems: 'flex-start' }}>

        {/* Palette */}
        {editMode && (
          <BlockPalette
            blockTypes={barConfig.blockTypes}
            onAddBlock={handleAddBlock}
          />
        )}

        {/* Grille du bar */}
        <div style={{ flex: 1 }}>
          {editMode && (
            <div style={{
              textAlign: 'center', marginBottom: '6px',
              fontSize: '12px', color: '#888',
            }}>
              Glissez les blocs pour former la forme de votre bar. Auto-sauvegarde active.
            </div>
          )}
          <BarLayout
            config={barConfig}
            blocks={blocks}
            setBlocks={setBlocks}
            slots={slots}
            onSlotClick={handleSlotClick}
            selectedSlotId={selectedSlotId}
            editMode={editMode}
            onDeleteBlock={handleDeleteBlock}
          />
        </div>
      </div>

      {/* Popup de selection produit */}
      {pickerOpen && (
        <ProductPicker
          products={products}
          currentSlot={slots[selectedSlotId]}
          onAssign={handleAssignProduct}
          onClear={handleClearSlot}
          onClose={() => { setPickerOpen(false); setSelectedSlotId(null); }}
        />
      )}
    </div>
  );
}

export default App;
